import React, { useState, useEffect } from 'react';
import { 
  MapPinIcon, 
  BuildingOfficeIcon, 
  PlusIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const AddressSelector = ({ 
  selectedAddress, 
  onAddressSelect, 
  companyData, 
  useCompanyAddress, 
  onUseCompanyAddressChange 
}) => {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    company_name: companyData?.name || '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    postal_code: '',
    phone: user?.phone || '',
    email: user?.email || '',
    is_default: false
  });

  useEffect(() => {
    loadAddresses();
  }, []);

  // Update form when user or company data changes
  useEffect(() => {
    setNewAddress(prev => ({
      ...prev,
      first_name: user?.first_name || prev.first_name,
      last_name: user?.last_name || prev.last_name,
      company_name: companyData?.name || prev.company_name,
      phone: user?.phone || prev.phone,
      email: user?.email || prev.email,
    }));
  }, [user, companyData]);

  const loadAddresses = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/addresses');
      setAddresses(response.data || []);
    } catch (error) {
      console.error('Error loading addresses:', error);
      toast.error('שגיאה בטעינת הכתובות');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('/api/addresses', newAddress);
      // Reload addresses instead of just adding to prevent duplicates
      await loadAddresses();
      // Reset only address-specific fields, keep user/company info
      setNewAddress({
        ...newAddress,
        address_line_1: '',
        address_line_2: '',
        city: '',
        postal_code: '',
        is_default: false
      });
      setShowAddForm(false);
      toast.success('הכתובת נוספה בהצלחה');
    } catch (error) {
      console.error('Error adding address:', error);
      toast.error('שגיאה בהוספת הכתובת');
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-20 bg-gray-200 rounded-lg"></div>
        <div className="h-20 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Company Address Option */}
      {companyData && companyData.address && (
        <label className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
          <input
            type="radio"
            name="address"
            checked={useCompanyAddress}
            onChange={() => {
              onUseCompanyAddressChange(true);
              onAddressSelect(null);
            }}
            className="mt-1 ml-3 text-primary-600 focus:ring-primary-500"
          />
          <div className="flex-1">
            <div className="flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 text-primary-600 ml-2" />
              <span className="font-medium text-gray-900">כתובת החברה</span>
              <span className="bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded-full mr-2">מומלץ</span>
            </div>
            <div className="text-sm text-gray-600 mt-1">
              <p className="font-medium">{companyData.name}</p>
              <p>{companyData.address}</p>
              <p>{companyData.city} {companyData.postal_code}</p>
              {companyData.phone && <p>טלפון: {companyData.phone}</p>}
            </div>
          </div>
        </label>
      )}

      {/* Saved Addresses */}
      {addresses.map((address) => (
        <label key={address.id} className="flex items-start p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
          <input
            type="radio"
            name="address"
            checked={selectedAddress?.id === address.id && !useCompanyAddress}
            onChange={() => {
              onAddressSelect(address);
              onUseCompanyAddressChange(false);
            }}
            className="mt-1 ml-3 text-primary-600 focus:ring-primary-500"
          />
          <div className="flex-1">
            <div className="flex items-center">
              <MapPinIcon className="h-5 w-5 text-gray-400 ml-2" />
              <span className="font-medium text-gray-900">
                {address.first_name} {address.last_name}
              </span>
              {address.is_default && (
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full mr-2">ברירת מחדל</span>
              )}
            </div>
            <div className="text-sm text-gray-600 mt-1">
              <p>{address.address_line_1} {address.address_line_2}</p>
              <p>{address.city} {address.postal_code}</p>
              {address.phone && <p>טלפון: {address.phone}</p>}
            </div>
          </div>
        </label>
      ))}

      {/* Add New Address Button */}
      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-primary-400 hover:bg-primary-50 transition-colors"
        >
          <PlusIcon className="h-5 w-5 text-gray-400 ml-2" />
          <span className="text-gray-600">הוסף כתובת משלוח חדשה</span>
        </button>
      )}

      {/* Add New Address Form */}
      {showAddForm && (
        <div className="border rounded-lg p-4 bg-gray-50">
          <h4 className="font-medium text-gray-900 mb-4 flex items-center">
            <MapPinIcon className="h-5 w-5 ml-2" />
            הוסף כתובת משלוח חדשה
          </h4>
          <form onSubmit={handleAddAddress} className="space-y-4">
            {/* Pre-filled user info (read-only for B2B users) */}
            <div className="bg-blue-50 p-3 rounded-md">
              <p className="text-sm text-blue-800 font-medium mb-2">פרטי המזמין:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">שם פרטי</label>
                  <input
                    type="text"
                    value={newAddress.first_name}
                    onChange={(e) => setNewAddress({...newAddress, first_name: e.target.value})}
                    className="w-full px-3 py-2 border border-blue-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">שם משפחה</label>
                  <input
                    type="text"
                    value={newAddress.last_name}
                    onChange={(e) => setNewAddress({...newAddress, last_name: e.target.value})}
                    className="w-full px-3 py-2 border border-blue-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
              </div>
              
              {companyData && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-blue-700 mb-1">חברה</label>
                  <input
                    type="text"
                    value={newAddress.company_name}
                    className="w-full px-3 py-2 border border-blue-200 rounded-md bg-blue-100 text-blue-800"
                    readOnly
                  />
                  <p className="text-xs text-blue-600 mt-1">החברה נקבעת אוטומטית על פי החשבון שלך</p>
                </div>
              )}
            </div>
            
            {/* Address fields */}
            <div className="space-y-4">
              <h5 className="font-medium text-gray-900">כתובת משלוח:</h5>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">כתובת *</label>
                <input
                  type="text"
                  value={newAddress.address_line_1}
                  onChange={(e) => setNewAddress({...newAddress, address_line_1: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="רחוב ומספר בית"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">כתובת נוספת (אופציונלי)</label>
                <input
                  type="text"
                  value={newAddress.address_line_2}
                  onChange={(e) => setNewAddress({...newAddress, address_line_2: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="דירה, קומה, וכו'"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">עיר *</label>
                  <input
                    type="text"
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">מיקוד *</label>
                  <input
                    type="text"
                    value={newAddress.postal_code}
                    onChange={(e) => setNewAddress({...newAddress, postal_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">טלפון</label>
                  <input
                    type="tel"
                    value={newAddress.phone}
                    onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">אימייל</label>
                  <input
                    type="email"
                    value={newAddress.email}
                    onChange={(e) => setNewAddress({...newAddress, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_default"
                checked={newAddress.is_default}
                onChange={(e) => setNewAddress({...newAddress, is_default: e.target.checked})}
                className="ml-2 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="is_default" className="text-sm text-gray-700">
                הגדר ככתובת ברירת מחדל
              </label>
            </div>
            
            <div className="flex space-x-3 space-x-reverse">
              <button
                type="submit"
                className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <CheckIcon className="h-4 w-4 ml-2" />
                שמור כתובת
              </button>
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                ביטול
              </button>
            </div>
          </form>
        </div>
      )}

      {/* No addresses message */}
      {addresses.length === 0 && !companyData?.address && !showAddForm && (
        <div className="text-center py-8 text-gray-500">
          <MapPinIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p>לא נמצאו כתובות שמורות</p>
          <p className="text-sm">הוסף כתובת משלוח כדי להמשיך</p>
        </div>
      )}
    </div>
  );
};

export default AddressSelector;